import React, { createContext, useState, useEffect, useContext } from 'react';
import api from '../services/api';
import googleAuth from '../services/googleAuth';
import { storage } from '../utils/helpers';
import { STORAGE_KEYS, API_ENDPOINTS } from '../utils/constants';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDemoMode, setIsDemoMode] = useState(false);

  useEffect(() => {
    // Check for demo mode in storage
    const demoMode = storage.get('demo_mode');
    if (demoMode) {
      setIsDemoMode(true);
      setUser({ name: 'Demo User', email: 'demo@example.com', isDemo: true });
    } else {
      const userData = storage.get(STORAGE_KEYS.USER_DATA);
      if (userData) setUser(userData);
    }
    setLoading(false);
  }, []);

  const handleGoogleCallback = async (params) => {
    try {
      const accessToken = params.get('accessToken');
      const refreshToken = params.get('refreshToken');
      const error = params.get('error');

      if (error) {
        return { success: false, error };
      }

      if (accessToken && refreshToken) {
        storage.set(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
        storage.set(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);

        try {
          const response = await api.get(API_ENDPOINTS.AUTH.USER_INFO);
          const userData = response.data.user;
          storage.set(STORAGE_KEYS.USER_DATA, userData);
          setUser(userData);
          setIsDemoMode(false);
          storage.remove('demo_mode');
          return { success: true };
        } catch (err) {
          const basicUserData = { authenticated: true };
          storage.set(STORAGE_KEYS.USER_DATA, basicUserData);
          setUser(basicUserData);
          return { success: true };
        }
      }

      return { success: false, error: 'No tokens received' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const loginWithGoogle = () => {
    googleAuth.login();
  };

  const enterDemoMode = () => {
    const demoUser = { name: 'Demo User', email: 'demo@example.com', isDemo: true };
    storage.set('demo_mode', true);
    storage.set(STORAGE_KEYS.USER_DATA, demoUser);
    setUser(demoUser);
    setIsDemoMode(true);
  };

  const exitDemoMode = () => {
    storage.remove('demo_mode');
    storage.remove(STORAGE_KEYS.USER_DATA);
    setUser(null);
    setIsDemoMode(false);
  };

  const logout = async () => {
    if (isDemoMode) {
      exitDemoMode();
      return;
    }
    
    try {
      await api.post(API_ENDPOINTS.AUTH.LOGOUT);
    } catch (error) {
      // Ignore logout API errors
    }
    storage.remove(STORAGE_KEYS.ACCESS_TOKEN);
    storage.remove(STORAGE_KEYS.REFRESH_TOKEN);
    storage.remove(STORAGE_KEYS.USER_DATA);
    storage.remove('demo_mode');
    setUser(null);
    setIsDemoMode(false);
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    isDemoMode,
    loginWithGoogle,
    handleGoogleCallback,
    enterDemoMode,
    exitDemoMode,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export default AuthContext;
